import * as React from 'react';
import { usePublicAppConfig } from '../../contexts/PublicAppConfigContext';
import { buildBrandAssetPath } from '../../lib/branding';
import './auth-layout.css';

export interface AuthBrandPanelProps {
  className?: string;
}

/**
 * Brand panel for auth pages
 * Displays logo and product messaging
 * Hidden on mobile, shown on desktop
 */
export const AuthBrandPanel: React.FC<AuthBrandPanelProps> = ({ className }) => {
  const { config, isLoading } = usePublicAppConfig();

  // Freeze the displayed brand values while the post-auth config fetch is in
  // flight. This prevents the left panel from updating mid-sign-in (e.g. while
  // the Google popup is open). Any workspace change goes through a full page
  // reload anyway, so the snapshot is always valid for the page lifetime.
  const frozenRef = React.useRef(config);
  if (!isLoading) frozenRef.current = config;
  const { authHeadline, authSupportingText, brandKey, productName } = frozenRef.current;
  const [logoUnavailable, setLogoUnavailable] = React.useState(false);
  const logoHref = buildBrandAssetPath(brandKey, 'logo');

  React.useEffect(() => {
    setLogoUnavailable(false);
  }, [logoHref]);

  return (
    <div className={`auth-brand-panel ${className || ''}`}>
      <div className="auth-brand-content">
        <div className="auth-brand-logo">
          {!logoUnavailable ? (
            <img
              src={logoHref}
              alt={`${productName} logo`}
              className="auth-brand-logo-icon"
              onError={() => setLogoUnavailable(true)}
            />
          ) : (
            <img src="/brand/cosow/logo.svg" alt="coSow logo" className="auth-brand-logo-icon" />
          )}
          <span className="auth-brand-logo-text">{productName}</span>
        </div>

        <div className="auth-brand-messaging">
          <h1 className="auth-brand-headline">{authHeadline}</h1>
          <p className="auth-brand-supporting">{authSupportingText}</p>
        </div>

        <div className="auth-brand-decoration" aria-hidden="true">
          <svg viewBox="0 0 200 200" fill="none">
            <circle
              cx="100"
              cy="100"
              r="80"
              stroke="var(--color-primary)"
              strokeWidth="0.5"
              opacity="0.2"
            />
            <circle
              cx="100"
              cy="100"
              r="60"
              stroke="var(--color-primary)"
              strokeWidth="0.5"
              opacity="0.3"
            />
            <circle
              cx="100"
              cy="100"
              r="40"
              stroke="var(--color-primary)"
              strokeWidth="0.5"
              opacity="0.4"
            />
          </svg>
        </div>
      </div>
    </div>
  );
};

AuthBrandPanel.displayName = 'AuthBrandPanel';
